import { Button, Text } from '@lmc/design-system'
import { cssVars } from '@lmc/design-system/tokens'
import { Form as AntdForm, type FormInstance, Modal } from 'antd'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { type ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

/** Asks before a change that would drop what is recorded. */
export type StepConfirmDiscard = {
  body: ReactNode
  /** Whether the change would drop anything, checked as it is made. */
  isNeeded: (form: FormInstance) => boolean
  /** Clears what the change drops, once confirmed, before it is made. */
  onConfirm?: (form: FormInstance) => void
  title: ReactNode
}

/** `ask(change)` makes the change, asking first when `confirm` says it drops something. */
export const useConfirmDiscard = (
  confirm: StepConfirmDiscard | undefined,
  testId: string | undefined
) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const form = AntdForm.useFormInstance()
  const [pending, setPending] = useState<(() => void) | null>(null)

  const ask = (change: () => void) =>
    confirm?.isNeeded(form) ? setPending(() => change) : change()

  const modal = confirm && (
    <Modal
      footer={
        <StepModalFooter>
          <Button
            data-testid={`${testId}-confirm-cancel`}
            onClick={() => setPending(null)}
            variant="secondary"
          >
            {t(incidentReportsConstants.CASE_V2_CONFIRM_KEEP)}
          </Button>
          <Button
            data-testid={`${testId}-confirm`}
            onClick={() => {
              confirm.onConfirm?.(form)
              pending?.()
              setPending(null)
            }}
            variant="critical"
          >
            {t(incidentReportsConstants.CASE_V2_CONFIRM_REMOVE)}
          </Button>
        </StepModalFooter>
      }
      onCancel={() => setPending(null)}
      open={pending !== null}
      title={
        <StepModalTitle forwardedAs="span" variant="heading-md" weight="medium">
          {confirm.title}
        </StepModalTitle>
      }
    >
      <Text>{confirm.body}</Text>
    </Modal>
  )

  return { ask, modal }
}

/**
 * A step modal's title: Heading M in sentence case, as designed. The legacy
 * ui-kit Modal.less uppercases every antd modal title.
 */
export const StepModalTitle = styled(Text)`
  text-transform: none;
`

/** A step modal's buttons, on the right. */
export const StepModalFooter = styled.div`
  display: flex;
  gap: ${cssVars.spacing[200]};
  justify-content: flex-end;
`
